import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ActionIcon, Group, Paper, Skeleton, Stack, Table, Text, Title } from '@mantine/core';
import dayjs from 'dayjs';
import { Icon, LoadMorePagination } from '@/components';
import { useDirectMessages } from '@/hooks/organization-programs';
import { directMessagesService } from '@/services/direct-messages.service';
import type { DirectMessageReportListing } from '@/types';
import styles from './DirectMessages.module.sass';

const LAYOUT = {
  LIMIT: 10,
  SKELETON_HEIGHT: 30,
  ACTION_ICON_SIZE: 20,
};

const DirectMessages = () => {
  const { id: churchId } = useParams<{ id: string }>();
  const [extraItems, setExtraItems] = useState<DirectMessageReportListing[]>([]);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
  const [exportingReportId, setExportingReportId] = useState<string | null>(null);

  const { data, isLoading } = useDirectMessages(churchId, { limit: LAYOUT.LIMIT });
  const firstPageItems = data?.items ?? [];
  const total = data?.total ?? 0;
  const items = useMemo(() => [...firstPageItems, ...extraItems], [firstPageItems, extraItems]);
  const hasMore = items.length < total;

  useEffect(() => {
    setExtraItems([]);
    if (firstPageItems.length > 0) {
      setCursor(firstPageItems[firstPageItems.length - 1].id);
    } else {
      setCursor(undefined);
    }
  }, [data, firstPageItems]);

  const handleLoadMore = async () => {
    if (!churchId || !hasMore || isLoadingMore) return;

    setIsLoadingMore(true);
    try {
      const response = await directMessagesService.getDirectMessages(churchId, { limit: LAYOUT.LIMIT, cursor });
      setExtraItems((prev) => [...prev, ...response.items]);
      if (response.items.length > 0) {
        setCursor(response.items[response.items.length - 1].id);
      }
    } catch (error) {
      console.error('Failed to load more direct messages reports:', error);
    } finally {
      setIsLoadingMore(false);
    }
  };

  const handleExportReport = async (reportId: string) => {
    if (!churchId) return;

    setExportingReportId(reportId);
    try {
      const blob = await directMessagesService.exportDirectMessageReport(churchId, reportId);
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = `direct-messages-report-${reportId}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (error) {
      console.error('Failed to export direct message report:', error);
    } finally {
      setExportingReportId(null);
    }
  };

  const skeletonRows = Array.from({ length: LAYOUT.LIMIT }).map((_, index) => (
    <Table.Tr key={`skeleton-${index}`}>
      <Table.Td>
        <Skeleton height={LAYOUT.SKELETON_HEIGHT} width="80%" radius="sm" />
      </Table.Td>
      <Table.Td>
        <Skeleton height={LAYOUT.SKELETON_HEIGHT} width="70%" radius="sm" />
      </Table.Td>
      <Table.Td>
        <Skeleton height={LAYOUT.SKELETON_HEIGHT} width="40%" radius="sm" />
      </Table.Td>
      <Table.Td className={styles.actionsColumn}>
        <Skeleton height={LAYOUT.ACTION_ICON_SIZE} width={LAYOUT.ACTION_ICON_SIZE} radius="sm" />
      </Table.Td>
    </Table.Tr>
  ));

  const rows = items.map((item) => (
    <Table.Tr key={item.id}>
      <Table.Td>{item.messageTitle}</Table.Td>
      <Table.Td>{dayjs(item.sentDate).format('MMM DD YYYY hh:mm A')}</Table.Td>
      <Table.Td>{item.recipientsCount}</Table.Td>
      <Table.Td className={styles.actionsColumn}>
        <Group gap="xs">
          <ActionIcon
            aria-label={`Export ${item.messageTitle} as CSV`}
            variant="subtle"
            loading={exportingReportId === item.id}
            onClick={() => handleExportReport(item.id)}
          >
            <Icon name="IconDownload" size={LAYOUT.ACTION_ICON_SIZE} />
          </ActionIcon>
        </Group>
      </Table.Td>
    </Table.Tr>
  ));

  if (!churchId) {
    return (
      <Paper p="xl" radius="md" w="100%">
        <Text c="red">Church ID not found</Text>
      </Paper>
    );
  }

  return (
    <Paper p="xl" radius="md" w="100%">
      <Stack gap="md">
        <Title order={3}>Direct Messages Report</Title>
        <Table striped highlightOnHover withRowBorders withColumnBorders withTableBorder>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Message Title</Table.Th>
              <Table.Th>Send Date</Table.Th>
              <Table.Th>Recipients Count</Table.Th>
              <Table.Th>Actions</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {isLoading ? skeletonRows : rows}
            {!isLoading && items.length === 0 && (
              <Table.Tr>
                <Table.Td colSpan={4}>
                  <Text c="dimmed">No direct message reports found</Text>
                </Table.Td>
              </Table.Tr>
            )}
          </Table.Tbody>
        </Table>
        <LoadMorePagination
          loaded={items.length}
          total={total}
          hasMore={hasMore}
          isLoading={isLoading}
          isLoadingMore={isLoadingMore}
          onLoadMore={handleLoadMore}
        />
      </Stack>
    </Paper>
  );
};

export default DirectMessages;
